/**
 * @param {'720p' | '1080p'} resolution
 * @returns {{ width: number; height: number }}
 */
export function veoCanvasDimensionsForResolution(resolution) {
  if (resolution === '1080p') {
    return { width: 1920, height: 1080 };
  }
  return { width: 1280, height: 720 };
}

/**
 * @param {string} src
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', () => reject(new Error('Could not load image.')));
    img.src = src;
  });
}

/**
 * @param {HTMLImageElement} image
 * @param {number} canvasWidth
 * @param {number} canvasHeight
 * @returns {HTMLCanvasElement}
 */
function renderContainFitCanvas(image, canvasWidth, canvasHeight) {
  const canvas = document.createElement('canvas');
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create canvas context.');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvasWidth, canvasHeight);

  const scale = Math.min(canvasWidth / image.naturalWidth, canvasHeight / image.naturalHeight);
  const drawWidth = image.naturalWidth * scale;
  const drawHeight = image.naturalHeight * scale;
  const x = (canvasWidth - drawWidth) / 2;
  const y = (canvasHeight - drawHeight) / 2;

  ctx.drawImage(image, x, y, drawWidth, drawHeight);
  return canvas;
}

/**
 * @param {HTMLCanvasElement} canvas
 * @returns {Promise<Blob>}
 */
function canvasToJpegBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Could not encode frame as JPEG.'));
        else resolve(blob);
      },
      'image/jpeg',
      0.95,
    );
  });
}

/**
 * @param {File} file
 * @param {{ width: number; height: number }} canvasSize
 * @returns {Promise<{
 *   blob: Blob;
 *   objectUrl: string;
 *   meta: {
 *     originalWidth: number;
 *     originalHeight: number;
 *     submittedWidth: number;
 *     submittedHeight: number;
 *     mimeType: string;
 *   };
 * }>}
 */
export async function prepareVeoFloorPlanFrame(file, canvasSize) {
  const objectUrlForLoad = URL.createObjectURL(file);
  try {
    const image = await loadImage(objectUrlForLoad);
    const originalWidth = image.naturalWidth;
    const originalHeight = image.naturalHeight;
    const canvas = renderContainFitCanvas(image, canvasSize.width, canvasSize.height);
    const blob = await canvasToJpegBlob(canvas);
    const objectUrl = URL.createObjectURL(blob);

    return {
      blob,
      objectUrl,
      meta: {
        originalWidth,
        originalHeight,
        submittedWidth: canvasSize.width,
        submittedHeight: canvasSize.height,
        mimeType: 'image/jpeg',
      },
    };
  } finally {
    URL.revokeObjectURL(objectUrlForLoad);
  }
}

/**
 * @param {number} width
 * @param {number} height
 * @returns {number}
 */
export function aspectRatioValue(width, height) {
  if (!width || !height) return 0;
  return width / height;
}

/**
 * @param {{
 *   first?: { originalWidth: number; originalHeight: number } | null;
 *   last?: { originalWidth: number; originalHeight: number } | null;
 * }} frames
 * @returns {boolean}
 */
export function framesHaveDimensionMismatch(frames) {
  const { first, last } = frames;
  if (!first?.originalWidth || !first?.originalHeight) return false;
  if (!last?.originalWidth || !last?.originalHeight) return false;

  if (first.originalWidth !== last.originalWidth || first.originalHeight !== last.originalHeight) {
    return true;
  }

  const firstRatio = aspectRatioValue(first.originalWidth, first.originalHeight);
  const lastRatio = aspectRatioValue(last.originalWidth, last.originalHeight);
  return Math.abs(firstRatio - lastRatio) > 0.01;
}
